import React from 'react';
import { format } from 'date-fns';
import { DollarSign, X } from 'lucide-react';
import { addEvent } from '../lib/firebase';
import { auth } from '../lib/firebase';

interface BalanceAdjustmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  date: Date;
  currentBalance: number;
}

export default function BalanceAdjustmentModal({
  isOpen,
  onClose,
  date,
  currentBalance
}: BalanceAdjustmentModalProps) {
  const [newBalance, setNewBalance] = React.useState(currentBalance.toFixed(2));
  const [loading, setLoading] = React.useState(false);

  // Reset the new balance when the current balance changes
  React.useEffect(() => {
    setNewBalance(currentBalance.toFixed(2));
  }, [currentBalance]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!auth.currentUser) return;

    setLoading(true);
    try {
      const targetBalance = parseFloat(newBalance);
      const adjustment = targetBalance - currentBalance;

      // Create a new adjustment event with the difference amount
      await addEvent({
        id: crypto.randomUUID(),
        title: 'Balance Adjustment',
        amount: targetBalance, // Store target balance for reference
        type: 'adjustment',
        date: new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0, 0), // Set to noon to avoid timezone issues
        userId: auth.currentUser.uid,
        isPaid: true,
        paidDate: new Date(),
        adjustmentAmount: adjustment // Store the actual adjustment amount
      });

      onClose();
    } catch (error) {
      console.error('Error adjusting balance:', error);
      alert('Failed to adjust balance. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="flex justify-between items-center p-6 border-b">
          <h3 className="text-lg font-medium text-gray-900">
            Adjust Balance for {format(date, 'MMMM d, yyyy')}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Current Balance: ${currentBalance.toFixed(2)}
            </label>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700">
                New Balance
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DollarSign className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  step="0.01"
                  value={newBalance}
                  onChange={(e) => setNewBalance(e.target.value)}
                  className="block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="0.00"
                  required
                />
              </div>
              {parseFloat(newBalance) !== currentBalance && (
                <p className="mt-2 text-sm text-gray-500">
                  Adjustment amount: ${(parseFloat(newBalance) - currentBalance).toFixed(2)}
                </p>
              )}
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || parseFloat(newBalance) === currentBalance}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? 'Adjusting...' : 'Adjust Balance'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}